import React from 'react';
import { ConnectionIssue } from '../sdk/PixelStreamingHandler';

interface ConnectionIssueOverlayProps {
  issue: ConnectionIssue | null;
  onRetry?: () => void;
  onDismiss?: () => void;
}

export const ConnectionIssueOverlay: React.FC<ConnectionIssueOverlayProps> = ({
  issue,
  onRetry,
  onDismiss
}) => {
  if (!issue) return null;

  const getIssueTitle = () => {
    switch (issue.type) {
      case 'websocket_closed':
        return 'Connection Lost';
      case 'afk_timeout':
        return 'Session Ended';
      case 'afk_warning':
        return 'Inactivity Warning';
      case 'afk_warning_update':
        return 'Stay Active';
      default:
        return 'Connection Issue';
    }
  };

  const getIssueIcon = () => {
    switch (issue.type) {
      case 'websocket_closed':
        return '⚠️';
      case 'afk_timeout':
        return '❌';
      case 'afk_warning':
        return '⏰';
      case 'afk_warning_update':
        return '👆';
      default:
        return '⚠️';
    }
  };

  const getIssueMessage = () => {
    switch (issue.type) {
      case 'afk_timeout':
        return 'Connection was disconnected due to inactivity. Please reload the page to reconnect.';
      case 'afk_warning':
        return 'You appear to be inactive. Move the mouse or click to maintain the connection.';
      case 'afk_warning_update':
        return 'Continue interacting with the stream to avoid disconnection.';
      default:
        return issue.message || 'A connection issue occurred.';
    }
  };

  const shouldShowRetryButton = () => {
    return issue.type === 'websocket_closed' || issue.type === 'afk_timeout';
  };

  const shouldShowDismissButton = () => {
    // For AFK warnings, we want to show a dismiss button instead of retry
    return issue.type === 'afk_warning' || issue.type === 'afk_warning_update' || onDismiss;
  };

  return (
    <div className={`connection-issue-overlay connection-issue-overlay--${issue.type}`}>
      <div className="connection-issue-overlay__content">
        <div className="connection-issue-overlay__icon">
          {getIssueIcon()}
        </div>
        
        <h2 className="connection-issue-overlay__title">
          {getIssueTitle()}
        </h2>
        
        <p className="connection-issue-overlay__message">
          {getIssueMessage()}
        </p>

        <div className="connection-issue-overlay__actions">
          {shouldShowRetryButton() && onRetry && (
            <button 
              className="connection-issue-overlay__button connection-issue-overlay__button--primary"
              onClick={onRetry}
            >
              Try Again
            </button>
          )}

          {shouldShowDismissButton() && onDismiss && (
            <button
              className="connection-issue-overlay__button connection-issue-overlay__button--secondary"
              onClick={onDismiss}
            >
              {issue.type === 'afk_warning' || issue.type === 'afk_warning_update' ? 'Got It' : 'Close'}
            </button>
          )}
        </div>

        {issue.code && (
          <div className="connection-issue-overlay__debug">
            <small>Code: {issue.code}</small>
            {issue.reason && <small> | Reason: {issue.reason}</small>}
          </div>
        )}
      </div>
    </div>
  );
}; 